import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TrendingUp, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="kiran-login-page min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src={`/login-wallpapers/wallpaper-${new Date().getDay()}.svg`}
          alt="NSE trading wallpaper" className="kiran-login-wallpaper absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="kiran-login-flashing-logo flex items-center gap-2 text-white">
            <div className="w-9 h-9 bg-white flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-slate-900" />
            </div>
            <span className="font-heading font-black text-xl tracking-tighter">KIRAN</span>
          </div>
          <div className="text-white">
            <h2 className="font-heading text-4xl font-black tracking-tighter leading-none mb-3">
              Build. Backtest.<br/>Auto-Trade.
            </h2>
            <p className="text-slate-300 text-sm max-w-sm">
              Connect any NSE broker, design option strategies with full strike & indicator control,
              and validate on up to 5 years of history before going live.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-slate-900 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter">KIRAN</span>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-8">Access your trading command center.</p>

          {error && <div data-testid="login-error" className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2">{error}</div>}

          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 mb-4 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600" required />

          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Password</label>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 mb-6 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600" required />

          <button data-testid="login-submit" disabled={busy}
            className="w-full h-10 bg-blue-600 text-white text-sm font-semibold rounded-sm hover:bg-blue-700 transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>

          <p className="text-sm text-slate-500 mt-6">
            New here? <Link to="/register" className="text-blue-600 font-semibold hover:underline" data-testid="go-register">Create account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
